import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  AiProcessedAttendee,
  Permission,
  StudentServiceAddAttendeeRequest,
  StudentServiceAddAttendeeResponse,
  StudentServiceTemplate,
  StudentServiceTemplateAttendee,
  StudentServiceTemplateEditResource,
  StudentServiceTemplateViewResource,
} from "generated/openApi";
import { useRouter } from "hooks";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { studentLinkPageRoute } from "./StudentLinkPage/StudentLinkPage";
import { StudentServiceTemplatePageBody } from "./StudentServiceTemplatePageBody/StudentServiceTemplatePageBody";
import { StudentServiceTemplatePageHeader } from "./StudentServiceTemplatePageHeader";

const routeName = "studentServiceTemplate";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {}

export const studentServiceTemplatePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "QR Code",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

const ROOT_PERMISSION: Permission = Permission.StudentServiceTemplate;

export const StudentServiceTemplatePage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const { matchTemplatePath } = useRouter();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<StudentServiceTemplate>({ requireSignature: true, isActive: true } as StudentServiceTemplate);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [template] });
  const [viewResource, setViewResource] = useState<StudentServiceTemplateViewResource>({} as any);
  const [editResource, setEditResource] = useState<StudentServiceTemplateEditResource>({});
  const [attendanceTabs, setAttendanceTabs] = useState<StudentServiceTemplateAttendee[]>();
  const [finishedFetch, setFinishedFetch] = useState<boolean>(false);
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [addAttendeeResponse, setAddAttendeeResponse] = useState<StudentServiceAddAttendeeResponse>({} as any);

  useEffect(() => {
    fetch(params.id);
    fetchViewResource(params.id);
    fetchEditResource()
  }, [params.id]);

  // useEffect(() => {
  //   if (params.crudMode === "edit") fetchEditResource();
  // }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${studentServiceTemplatePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`
    );

  const fetch = (id: number) => {
    ApiService.studentServiceTemplates.get(id).then((response) => {
      setFinishedFetch(true);
      setTemplate(response);
      resetUnsavedChanges([response]);
    });
    ApiService.studentServiceTemplates.getServiceTemplateAttendees(id).then((response) => {
      setAttendanceTabs(response);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.studentServiceTemplates.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentServiceTemplates.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const addAttendees = async (request: StudentServiceAddAttendeeRequest) => {
    var result = await ApiService.studentServiceTemplates.addAttendees(request);
    setAddAttendeeResponse(result);
  }

  const pageHeader = (
    <StudentServiceTemplatePageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentServiceTemplatePageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      template={template}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentServiceTemplatePageBody
      finishedFetch={finishedFetch}
      rootPermission={ROOT_PERMISSION}
      crudMode={params.crudMode}
      template={template}
      addAttendee={addAttendees}
      attendanceTabs={attendanceTabs}
      setTemplate={setTemplate}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
      studentServiceAddResponse={addAttendeeResponse}
    />
  );

  return (
    <Page isForm header={pageHeader}>
      {pageBody}
    </Page>
  );
};
